import styles from "./LibraryItems.module.css";
import { useState, useEffect, useMemo, useRef } from "react";
import Pagination from "../Pagination/Pagination";
import Asset from "../AssetsGrid/Asset";
import ListItem from "../AssetsGrid/ListItem";
import Filters from "../Filters/Filters";
import Toggle from "../Forms/Toggle";
import ViewToggle from "./ViewToggle";
import SortDropdown from "./SortDropdown";
import AdaptFilters from "../LibraryAdaptFilters/AdaptFilters";
import LensRequestStrip from "../LensRequestStrip/LensRequestStrip";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  fetchProducts,
  fetchLibraryFilterOptions,
  getFilterSnapshotBucket,
} from "../../store/slices/productSlice";
import isReactSnap from "../../utils/isReactSnap";
import {
  buildNormalizedLibrarySearchParams,
  normalizeLibraryAccessType,
} from "../../utils/libraryAccessType";
import {
  buildSelectedLibraryFilters,
  getFilterSnapshotManufacturers,
  normalizeLensSeriesKey,
} from "../../utils/libraryFilters";
import {
  buildLibraryAnalyticsProps,
  buildProductAnalyticsProps,
  capture,
  normalizeSearchTerm,
} from "../../analytics";

const VALID_VIEWS = ["grid", "list"];
const VALID_SORTS = ["date", "name_asc", "name_desc", "price_asc", "price_desc"];

const LibraryItems = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { products, status } = useSelector((state) => state.products);
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingPageChangeRef = useRef(null);
  const lastFilterAnalyticsKeyRef = useRef("");
  const lastSearchAnalyticsValueRef = useRef("");
  const lastEmptyResultsAnalyticsKeyRef = useRef("");
  const hasAnimatedInitialResultsRef = useRef(false);
  const pageTransitionStartedAtRef = useRef(0);
  const hasSeenPageLoadingRef = useRef(false);
  const [isPageTransitionPending, setIsPageTransitionPending] = useState(false);
  const [showUpdateHint, setShowUpdateHint] = useState(false);
  const urlCameras = searchParams.getAll("camera");
  const urlLensSeries = searchParams.getAll("lens_series");
  const urlSearch = searchParams.get("search") || "";
  const filterBucketKey = getFilterSnapshotBucket(searchParams.get('access_type'));
  const searchParamsKey = searchParams.toString();
  const filterSnapshot = useSelector((state) => state.products.filterSnapshots[filterBucketKey]);
  const filtersStatus = useSelector((state) => state.products.filterStatusByAccessType[filterBucketKey]);

  const [view, setView] = useState(() => {
    const initialUrlView = searchParams.get("view");
    if (VALID_VIEWS.includes(initialUrlView)) {
      return initialUrlView;
    }
    if (typeof window === "undefined") {
      return "grid";
    }
    const savedView = localStorage.getItem("library_view");
    return VALID_VIEWS.includes(savedView) ? savedView : "grid";
  });
  const [sort, setSort] = useState(() => {
    const initialUrlSort = searchParams.get("sort");
    if (VALID_SORTS.includes(initialUrlSort)) {
      return initialUrlSort;
    }
    if (typeof window === "undefined") {
      return "date";
    }
    const savedSort = localStorage.getItem("library_sort");
    return VALID_SORTS.includes(savedSort) ? savedSort : "date";
  });

  const [openLensBrand, setOpenLensBrand] = useState([]);
  const [openManufacturer, setOpenManufacturer] = useState([]);
  const [openCamera, setOpenCamera] = useState([]);

  const clearPendingPageChange = () => {
    if (typeof window === "undefined") {
      return;
    }

    if (pendingPageChangeRef.current) {
      window.clearTimeout(pendingPageChangeRef.current);
      pendingPageChangeRef.current = null;
    }
  };

  const [search, setSearch] = useState(urlSearch);
  const manufacturerTree = useMemo(
    () => getFilterSnapshotManufacturers(filterSnapshot),
    [filterSnapshot]
  );
  const allManufacturerNames = useMemo(
    () => manufacturerTree.map((manufacturer) => manufacturer?.name).filter(Boolean),
    [manufacturerTree]
  );

  // Camera brands start expanded. Applied once, so a manual collapse survives
  // later filter-snapshot refetches.
  const didExpandManufacturersRef = useRef(false);
  useEffect(() => {
    if (didExpandManufacturersRef.current || allManufacturerNames.length === 0) {
      return;
    }

    didExpandManufacturersRef.current = true;
    setOpenManufacturer(allManufacturerNames);
  }, [allManufacturerNames]);

  const effectiveLensSeriesKeys = useMemo(
    () => Array.from(new Set(urlLensSeries.map((value) => normalizeLensSeriesKey(value)).filter(Boolean))),
    [urlLensSeries],
  );

  useEffect(() => {
    const normalizedSearchParams = buildNormalizedLibrarySearchParams(searchParams);
    if (normalizedSearchParams.toString() === searchParamsKey) {
      return;
    }

    setSearchParams(normalizedSearchParams, { replace: true });
  }, [searchParams, searchParamsKey, setSearchParams]);

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(searchParamsKey);

    if (typeof window === 'undefined') return;
    const savedView = localStorage.getItem('library_view');
    const urlView = currentSearchParams.get('view');
    const nextView = VALID_VIEWS.includes(urlView)
      ? urlView
      : VALID_VIEWS.includes(savedView)
        ? savedView
        : null;

    if (nextView) {
      setView((prev) => (prev === nextView ? prev : nextView));
      localStorage.setItem('library_view', nextView);
    }

    const savedSort = localStorage.getItem('library_sort');
    const urlSort = currentSearchParams.get('sort');
    const nextSort = VALID_SORTS.includes(urlSort)
      ? urlSort
      : VALID_SORTS.includes(savedSort)
        ? savedSort
        : null;

    if (nextSort) {
      setSort((prev) => (prev === nextSort ? prev : nextSort));
      localStorage.setItem('library_sort', nextSort);
    }
  }, [searchParamsKey]);

  useEffect(() => {
    if (isReactSnap) return;
    const request = dispatch(fetchLibraryFilterOptions({ accessType: filterBucketKey }));
    return () => {
      request.abort();
    };
  }, [dispatch, filterBucketKey]);

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(searchParamsKey);
    const lensSeries = Array.from(
      new Set(
        currentSearchParams
          .getAll("lens_series")
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean),
      ),
    );
    const page = currentSearchParams.get('page') || 1;
    const pageSize = view === 'list' ? 15 : 24;
    const camera = currentSearchParams.getAll('camera');
    const product = currentSearchParams.getAll('product');
    const accessType = normalizeLibraryAccessType(currentSearchParams.get('access_type') || '');
    const search = currentSearchParams.get('search') || '';
    const effectiveSort = sort && sort !== 'date' ? sort : '';
    const full_price__gte = currentSearchParams.get('full_price__gte') || '';
    const full_price__lte = currentSearchParams.get('full_price__lte') || '';
    if (isReactSnap) return;
    const request = dispatch(fetchProducts({
      page,
      pageSize,
      camera,
      lensSeries,
      product,
      accessType,
      full_price__gte,
      full_price__lte,
      search,
      sort: effectiveSort,
    }));

    return () => {
      request.abort();
    };
  }, [dispatch, searchParamsKey, sort, view]);

  useEffect(() => {
    setSearch(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth <= 1200) return;

    const handleFirstTab = (event) => {
      if (event.key !== "Tab" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const activeElement = document.activeElement;
      if (activeElement && activeElement !== document.body && activeElement !== document.documentElement) {
        disarm();
        return;
      }

      const searchInput = document.getElementById("library-search");

      if (!searchInput) {
        disarm();
        return;
      }

      event.preventDefault();
      searchInput.focus();
      disarm();
    };

    const disarm = () => {
      document.removeEventListener("keydown", handleFirstTab, true);
      window.removeEventListener("pointerdown", disarm, true);
    };

    document.addEventListener("keydown", handleFirstTab, true);
    window.addEventListener("pointerdown", disarm, true);

    return disarm;
  }, []);

  const selected = useMemo(() => buildSelectedLibraryFilters({
    manufacturers: manufacturerTree,
    cameraIds: urlCameras,
    lensSeriesKeys: effectiveLensSeriesKeys,
  }), [manufacturerTree, urlCameras, effectiveLensSeriesKeys]);

  const displayedProducts = products?.results || [];

  const toggleManufacturerMenu = (name) => setOpenManufacturer((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  const toggleCameraMenu = (id) => { const k = id?.toString(); setOpenCamera((prev) => prev.includes(k) ? prev.filter((i) => i !== k) : [...prev, k]); };
  const toggleLensBrandMenu = (key) => setOpenLensBrand((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const handleCameraSelect = (cameraObj, event) => {
    event.stopPropagation();
    startResultsTransition();
    const cameraIdStr = cameraObj.id.toString();
    const currentIds = searchParams.getAll("camera");
    const newIds = currentIds.includes(cameraIdStr) ? currentIds.filter(id => id !== cameraIdStr) : [...currentIds, cameraIdStr];
    const p = new URLSearchParams(searchParams);
    p.delete("camera");
    newIds.forEach(id => p.append("camera", id));
    p.set("page", "1");
    setSearchParams(p);
  };

  const handleLensBrandSelect = (lensBrand) => {
    startResultsTransition();
    const allSeriesKeys = (lensBrand.seriesGroups || [])
      .map((sg) => String(sg.key || "").trim().toLowerCase())
      .filter(Boolean);

    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      const existingKeys = new Set(
        p.getAll("lens_series").map((k) => k.trim().toLowerCase())
      );
      const allSelected = allSeriesKeys.every((k) => existingKeys.has(k));

      if (allSelected) {
        allSeriesKeys.forEach((k) => existingKeys.delete(k));
      } else {
        allSeriesKeys.forEach((k) => existingKeys.add(k));
      }

      p.delete("lens_series");
      Array.from(existingKeys).forEach((k) => p.append("lens_series", k));
      p.set("page", "1");
      return p;
    });
  };

  const handleLensSeriesSelect = (lensSeries) => {
    startResultsTransition();
    const seriesKey = String(lensSeries?.key || "").trim().toLowerCase();
    if (!seriesKey) {
      return;
    }

    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const currentKeys = new Set(
        p.getAll("lens_series").map((value) => value.trim().toLowerCase())
      );
      if (currentKeys.has(seriesKey)) {
        currentKeys.delete(seriesKey);
      } else {
        currentKeys.add(seriesKey);
      }

      p.delete("lens_series");
      Array.from(currentKeys).forEach((value) => p.append("lens_series", value));
      p.set("page", "1");
      return p;
    });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    const p = new URLSearchParams(searchParams);
    if (value) { p.set("search", value); } else { p.delete("search"); }
    p.set("page", "1");
    setSearchParams(p);
  };

  const resetAllFilters = () => {
    startResultsTransition();
    setSearch("");
    setOpenManufacturer(allManufacturerNames);
    setOpenCamera([]);
    setOpenLensBrand([]);
    const p = new URLSearchParams();
    p.set("page", "1");
    if (view !== "grid") p.set("view", view);
    if (sort !== "date") p.set("sort", sort);
    setSearchParams(p);
  };

  const handleViewChange = (newView) => {
    if (newView === view) {
      return;
    }

    capture("library_view_changed", {
      ...buildLibraryAnalyticsProps({
        searchParams,
        view: newView,
        sort,
        resultCount: products?.results?.length || 0,
        totalCount: products?.count || 0,
      }),
      previous_view: view,
    });

    hasAnimatedInitialResultsRef.current = false;
    startResultsTransition();
    if (typeof window !== "undefined") {
      localStorage.setItem("library_view", newView);
    }
    setView(newView);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (newView === "grid") {
        p.delete("view");
      } else {
        p.set("view", newView);
      }
      p.set("page", "1");
      return p;
    });
  };

  const handleSortChange = (newSort) => {
    if (newSort === sort) {
      return;
    }

    capture("library_sorted", {
      ...buildLibraryAnalyticsProps({
        searchParams,
        view,
        sort: newSort,
        resultCount: products?.results?.length || 0,
        totalCount: products?.count || 0,
      }),
      previous_sort: sort,
    });

    startResultsTransition();
    if (typeof window !== "undefined") {
      localStorage.setItem("library_sort", newSort);
    }
    setSort(newSort);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (newSort === "date") {
        p.delete("sort");
      } else {
        p.set("sort", newSort);
      }
      p.set("page", "1");
      return p;
    });
  };

  const activeSidebarFilterCount =
    selected.cameras.length
    + effectiveLensSeriesKeys.length
    + (search ? 1 : 0)
    + (searchParams.get("full_price__gte") ? 1 : 0)
    + (searchParams.get("full_price__lte") ? 1 : 0);

  const filterProps = {
    manufacturers: manufacturerTree,
    selected,
    openManufacturer,
    openCamera,
    openLensBrand,
    toggleManufacturerMenu,
    toggleCameraMenu,
    toggleLensBrandMenu,
    handleCameraSelect,
    handleLensBrandSelect,
    handleLensSeriesSelect,
    handleSearch,
    search,
    activeFilterCount: activeSidebarFilterCount,
    onResetFilters: resetAllFilters,
  };

  const totalCount = products?.count || 0;
  const currentPage = Number(searchParams.get('page') || 1);
  const currentPageSize = view === 'list' ? 15 : Number(searchParams.get('page_size') || 24);
  const currentResultsCount = products?.results?.length || 0;
  const totalPages = Math.ceil(totalCount / currentPageSize);
  const shouldShowTopPagination = totalPages > 1;
  const shouldShowBottomPagination = totalPages > 1 && currentResultsCount > 8;
  const hasActiveFilters = Array.from(searchParams.keys()).some(
    (key) => key !== "page" && key !== "page_size" && key !== "view" && key !== "sort"
  );
  const shouldAnimateCards = !hasAnimatedInitialResultsRef.current;
  const isResultsRefreshing =
    isPageTransitionPending ||
    (status === "loading" && currentResultsCount > 0) ||
    (filtersStatus === "loading" && currentResultsCount > 0);

  useEffect(() => {
    if (status !== "succeeded" || totalCount !== 0 || !hasActiveFilters) {
      return;
    }

    const emptyResultsKey = searchParams.toString();
    if (emptyResultsKey === lastEmptyResultsAnalyticsKeyRef.current) {
      return;
    }

    lastEmptyResultsAnalyticsKeyRef.current = emptyResultsKey;
    capture("empty_search_results_viewed", buildLibraryAnalyticsProps({
      searchParams,
      view,
      sort,
      resultCount: currentResultsCount,
      totalCount,
    }));
  }, [status, totalCount, hasActiveFilters, searchParams, view, sort, currentResultsCount]);

  useEffect(() => {
    if (status !== "succeeded") return;

    const normalizedSearch = normalizeSearchTerm(urlSearch);
    if (!normalizedSearch || normalizedSearch === lastSearchAnalyticsValueRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (normalizedSearch === lastSearchAnalyticsValueRef.current) return;
      lastSearchAnalyticsValueRef.current = normalizedSearch;
      capture("library_search_changed", buildLibraryAnalyticsProps({
        searchParams,
        view,
        sort,
        resultCount: currentResultsCount,
        totalCount,
      }));
    }, 600);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status, urlSearch, searchParams, view, sort, currentResultsCount, totalCount]);

  useEffect(() => {
    if (status !== "succeeded") return;

    const filterKey = JSON.stringify({
      camera: urlCameras.slice().sort(),
      lens_series: effectiveLensSeriesKeys.slice().sort(),
      access_type: searchParams.get("access_type") || "",
      full_price__gte: searchParams.get("full_price__gte") || "",
      full_price__lte: searchParams.get("full_price__lte") || "",
      product: searchParams.getAll("product").slice().sort(),
    });

    if (filterKey === lastFilterAnalyticsKeyRef.current) {
      return;
    }

    if (lastFilterAnalyticsKeyRef.current === "") {
      lastFilterAnalyticsKeyRef.current = filterKey;
      return;
    }

    lastFilterAnalyticsKeyRef.current = filterKey;
    capture("library_filters_changed", buildLibraryAnalyticsProps({
      searchParams,
      view,
      sort,
      resultCount: currentResultsCount,
      totalCount,
    }));
  }, [
    status,
    urlCameras,
    effectiveLensSeriesKeys,
    searchParams,
    view,
    sort,
    currentResultsCount,
    totalCount,
  ]);

  useEffect(() => {
    if (
      !hasAnimatedInitialResultsRef.current &&
      status === "succeeded" &&
      currentResultsCount > 0
    ) {
      hasAnimatedInitialResultsRef.current = true;
    }
  }, [status, currentResultsCount]);

  useEffect(() => {
    return () => {
      clearPendingPageChange();
    };
  }, []);

  useEffect(() => {
    if (!isPageTransitionPending) return;

    if (status === "loading") {
      hasSeenPageLoadingRef.current = true;
      return;
    }

    if (!hasSeenPageLoadingRef.current) {
      return;
    }

    const elapsed = Date.now() - pageTransitionStartedAtRef.current;
    const remaining = Math.max(0, 220 - elapsed);

    const timeoutId = window.setTimeout(() => {
      setIsPageTransitionPending(false);
      hasSeenPageLoadingRef.current = false;
    }, remaining);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status, isPageTransitionPending]);

  useEffect(() => {
    let timeoutId;

    if (isPageTransitionPending) {
      setShowUpdateHint(true);
      return;
    }

    if (isResultsRefreshing) {
      timeoutId = window.setTimeout(() => {
        setShowUpdateHint(true);
      }, 240);
    } else {
      setShowUpdateHint(false);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isResultsRefreshing, isPageTransitionPending]);

  const startResultsTransition = () => {
    setIsPageTransitionPending(true);
    pageTransitionStartedAtRef.current = Date.now();
    hasSeenPageLoadingRef.current = false;
  };

  const updatePage = (page) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('page', page);
      return p;
    });
  };

  const handlePageChange = (page, { scrollToTop = false } = {}) => {
    if (page === currentPage) {
      return;
    }

    startResultsTransition();

    if (scrollToTop && typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!scrollToTop || typeof window === "undefined") {
      updatePage(page);
      return;
    }

    clearPendingPageChange();

    const getScrollTop = () => Math.max(
      window.scrollY || 0,
      document.documentElement?.scrollTop || 0,
      document.body?.scrollTop || 0
    );

    if (getScrollTop() <= 4) {
      updatePage(page);
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    document.body.scrollTo({ top: 0, behavior: "smooth" });

    const startedAt = window.performance?.now?.() ?? Date.now();

    const finalizePageChange = () => {
      clearPendingPageChange();
      updatePage(page);
    };

    const watchScrollTop = () => {
      const elapsed = (window.performance?.now?.() ?? Date.now()) - startedAt;

      if (getScrollTop() <= 4 || elapsed >= 520) {
        finalizePageChange();
        return;
      }

      pendingPageChangeRef.current = window.setTimeout(watchScrollTop, 16);
    };

    pendingPageChangeRef.current = window.setTimeout(watchScrollTop, 16);
  };

  const showAllProducts = () => {
    startResultsTransition();
    setSearch("");
    setOpenManufacturer(allManufacturerNames);
    setOpenCamera([]);
    setOpenLensBrand([]);
    const p = new URLSearchParams();
    p.set("page", "1");
    if (view !== "grid") p.set("view", view);
    if (sort !== "date") p.set("sort", sort);
    setSearchParams(p);
  };

  const returnTo = `${location.pathname}${location.search}`;
  const captureProductClick = (item, index) => {
    capture("library_product_clicked", {
      ...buildProductAnalyticsProps(item),
      ...buildLibraryAnalyticsProps({
        searchParams,
        view,
        sort,
        resultCount: currentResultsCount,
        totalCount,
      }),
      source_context: "library",
      result_position: ((currentPage - 1) * currentPageSize) + index + 1,
      result_index: index,
    });
  };

  return (
    <div className={styles.globalContainer}>
      <section style={{ paddingTop: "0px" }} className={`height ${styles.main}`}>
        <div className={styles.filterscont}>
          <AdaptFilters {...filterProps} onBeforeAvailabilityChange={startResultsTransition} />
        </div>

        <div className={styles.body}>
          {/* Sidebar: filters only */}
          <div className={styles.filterwrap}>
            <Filters {...filterProps} />
          </div>

          <div className={styles.assetscont}>
            {/* Controls bar: pagination + count + free toggle — always visible */}
            <div className={styles.controlsBar}>
              <div className={styles.contpag}>
                {shouldShowTopPagination && (
                  <Pagination
                    compact
                    customClassName={styles.customPagination}
                    customPageSize={currentPageSize}
                    currentPage={currentPage}
                    onPageChange={(page) => handlePageChange(page)}
                    pagination={{ count: products.count, next: products.next, previous: products.previous }}
                  />
                )}
              </div>
              {showUpdateHint && (
                <span className={styles.updateHint} aria-live="polite">
                  <span className={styles.updateDot} aria-hidden="true" />
                  Updating
                </span>
              )}
              <div className={styles.controlsRight}>
                <ViewToggle view={view} onViewChange={handleViewChange} />
                <SortDropdown sort={sort} onSortChange={handleSortChange} />
                <span className={styles.resultsCount}>
                  {totalCount} {totalCount === 1 ? 'result' : 'results'}
                </span>
                <div className={styles.freeToggleWrap}>
                  <Toggle
                    variant="segmented"
                    label="Availability filter"
                    onBeforeChange={startResultsTransition}
                  />
                </div>
              </div>
            </div>

            {displayedProducts.length > 0 ? (
              /* Stale-while-revalidate: old cards stay visible, dim on error */
              <div
                className={`${styles.resultsArea} ${showUpdateHint ? styles.resultsAreaPending : ""}`}
                style={status === 'failed' ? { opacity: 0.4, transition: 'opacity 200ms ease-out' } : undefined}
              >
                {view === 'list' ? (
                  <div className={styles.list}>
                    {displayedProducts.map((item, i) => (
                      <ListItem
                        key={item.id}
                        asset={item}
                        animateIn={shouldAnimateCards}
                        index={i}
                        returnTo={returnTo}
                        onProductClick={() => captureProductClick(item, i)}
                      />
                    ))}
                  </div>
                ) : (
                   <div className={styles.assets}>
                     {displayedProducts.map((item, i) => (
                        <Asset
                        key={item.id}
                        asset={item}
                        animateIn={shouldAnimateCards}
                        returnTo={returnTo}
                        onProductClick={() => captureProductClick(item, i)}
                      />
                    ))}
                  </div>
                )}
                {shouldShowBottomPagination && (
                  <div style={{ marginTop: 'auto' }}>
                    <Pagination
                      compact
                      customPageSize={currentPageSize}
                      currentPage={currentPage}
                      onPageChange={(page) => handlePageChange(page, { scrollToTop: true })}
                      pagination={{ count: products.count, next: products.next, previous: products.previous }}
                    />
                  </div>
                )}
              </div>
            ) : status === 'loading' ? (
              /* Skeleton: initial load only (no previous data) */
              <div className={styles.resultsArea}>
                {view === 'list' ? (
                  <div className={styles.list}>
                    {Array.from({ length: currentPageSize }).map((_, i) => (
                      <div key={i} className={styles.skeletonListItem} />
                    ))}
                  </div>
                ) : (
                  <div className={styles.assets}>
                    {Array.from({ length: currentPageSize }).map((_, i) => (
                      <div key={i} className={styles.skeletonCard} />
                    ))}
                  </div>
                )}
              </div>
            ) : status === 'succeeded' ? (
              <div className={styles.noitem}>
                <div className={styles.message}>
                  <h2 className="h3-medium">
                    {hasActiveFilters ? "No matching products" : "No products available"}
                  </h2>
                  <p className="h4-medium">
                    {hasActiveFilters
                      ? "Try clearing the filters, or tell us which camera and lens you need and we will look into shooting it."
                      : "There are no products in the library yet. Please check back later."}
                  </p>
                  {/* Both actions sit on one row under a single message: the
                      duplicate heading the request strip used to bring read as
                      a second, near-identical empty state. Same condition as
                      the empty_search_results_viewed event below, so the
                      request clicks and that event stay comparable. */}
                  {hasActiveFilters && (
                    <div className={styles.emptyActions}>
                      <button
                        type="button"
                        className={styles.emptyLink}
                        onClick={showAllProducts}
                      >
                        <span>Show all products</span>
                        <span className={styles.emptyLinkArrow}>→</span>
                      </button>
                      <LensRequestStrip
                        variant="empty"
                        linkOnly
                        linkClassName={styles.emptyLink}
                        arrowClassName={styles.emptyLinkArrow}
                        view={view}
                        sort={sort}
                        resultCount={currentResultsCount}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {(status === "failed" || filtersStatus === "failed") && (
        <div className={styles.errorOverlay}>
          <h2 className="h2-medium">
            {status === "failed" && filtersStatus === "failed"
              ? "Something went wrong with both data and filters"
              : status === "failed"
                ? "Something went wrong with data loading"
                : "Something went wrong with filters loading"}
          </h2>
          <p className="h4-medium">Please try refreshing the page or contact support.</p>
        </div>
      )}
    </div>
  );
};

export default LibraryItems;
